import { withResponsiveOptions, withIntersectionObserver } from '@studiometa/js-toolkit';
import { nextTick } from '@studiometa/js-toolkit/utils';
import {
  Slider as SliderCore,
  SliderItem,
  SliderDrag,
  SliderBtn,
  SliderDots,
} from '@studiometa/ui';

/**
 * Slider component
 */
export default class SliderAuto extends withIntersectionObserver(
  withResponsiveOptions(SliderCore),
) {
  /**
   * Component Configuration
   */
  static config = {
    ...SliderCore.config,
    name: 'SliderAuto',
    refs: ['overlay[]', 'progress[]', 'insets[]', ...SliderCore.config.refs],
    components: {
      SliderItem,
      SliderDrag,
      SliderBtn,
      SliderDots,
    },
    options: {
      ...SliderCore.options,
      showProgress: {
        type: String,
        responsive: true,
      },
      delay: {
        type: Number,
        default: 6000,
        responsive: true,
      },
    },
    observer: {
      rootMargin: '0px',
      threshold: 0.25,
    },
  };

  /**
   * Mounted
   */
  mounted() {
    super.mounted();
    this.timer();
    nextTick(() => {
      this.goTo(0);
    });
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  intersected(entries) {
    if (entries[0].isIntersecting) {
      this.timer();
      this.$children.SliderItem.forEach((item) => {
        const video = item.$el.querySelector('video');
        if (video) {
          video.play();
        }
      });
      this.showOverlay(this.currentIndex);
    } else {
      clearInterval(this.interval);
      this.$children.SliderItem.forEach((item) => {
        const video = item.$el.querySelector('video');
        if (video) {
          video.pause();
        }
      });
      this.pauseOverlay(this.currentIndex);
    }
  }

  /**
   * @param {object} root0
   * @param {number} root0.index
   */
  onInsetsMouseenter({ index }) {
    if (index === this.currentIndex) {
      this.pauseOverlay(index);
      clearInterval(this.interval);
    }
  }

  /**
   * @param {object} root0
   * @param {number} root0.index
   */
  onInsetsMouseleave({ index }) {
    if (index === this.currentIndex) {
      this.showOverlay(index);
      this.timer();
    }
  }

  /**
   *
   */
  onDocumentVisibilitychange() {
    if (document.hidden) {
      clearInterval(this.interval);
      this.pauseOverlay(this.currentIndex);
      this.$children.SliderItem.forEach((item) => {
        const video = item.$el.querySelector('video');
        if (video) {
          video.pause();
        }
      });
    } else {
      this.timer();
      this.showOverlay(this.currentIndex);
      this.$children.SliderItem.forEach((item) => {
        const video = item.$el.querySelector('video');
        if (video) {
          video.play();
        }
      });
    }
  }

  /**
   * Set an interval to change slide
   */
  timer() {
    clearInterval(this.interval);
    this.interval = setInterval(() => {
      if (this.currentIndex < this.indexMax) {
        this.goNext();
      } else {
        this.goTo(0);
      }
    }, this.$options.delay);
  }

  /**
   * @param {number} index
   */
  goTo(index) {
    super.goTo(index);
    clearInterval(this.interval);
    this.timer();

    if (this.$options.showProgress === 'true' && this.$refs.progress) {
      this.$refs.progress.forEach((el, i) => {
        if (i === index) {
          this.showProgress(i);
        } else {
          this.hideProgress(i);
        }
      });
    } else if (this.$options.showProgress === 'false' && this.$refs.overlay) {
      this.$refs.overlay.forEach((el, i) => {
        if (i === index) {
          this.showOverlay(i);
        } else {
          this.hideOverlay(i);
        }
      });
    }
  }

  /**
   * @param {number} index
   */
  showOverlay(index) {
    const el = this.$refs.overlay[index];
    el.classList.remove('scale-x-0');
    el.style.transition = `transform ${this.$options.delay}ms linear`;
    el.style.transform = 'scaleX(1)';
  }

  /**
   * @param {number} index
   */
  pauseOverlay(index) {
    const el = this.$refs.overlay[index];
    if (!el) return;

    const computedStyle = window.getComputedStyle(el);
    const matrix = computedStyle.transform;

    // Extract the scaleX value from the transformation matrix to resume the animation
    if (matrix && matrix !== 'none') {
      const matrixValues = matrix.match(/matrix\((.*)\)/);
      if (matrixValues) {
        const currentScaleX = matrixValues[1].split(', ')[0];
        el.style.transition = 'none';
        el.style.transform = `scaleX(${currentScaleX})`;
      }
    }
  }

  /**
   * Hide the overlay
   * @param {number} index
   */
  hideOverlay(index) {
    const el = this.$refs.overlay[index];
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'scaleX(0)';
    el.classList.add('scale-x-0');
  }

  /**
   * @param {number} index
   */
  showProgress(index) {
    const el = this.$refs.progress[index];
    if (!el) return;
    el.style.transition = `transform ${this.$options.delay}ms linear`;
    el.style.transform = 'scaleX(1)';
  }

  /**
   * @param {number} index
   */
  hideProgress(index) {
    const el = this.$refs.progress[index];
    if (!el) return;
    el.style.transition = 'none';
    el.style.transform = 'scaleX(0)';
    el.classList.add('scale-x-0');
  }
}
